import { Outlet, useLocation } from 'react-router';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Header } from './Header.tsx';
import { PageSkeleton } from './PageSkeleton.tsx';
import { LimitsFoot, PageAside } from './PageAside.tsx';
import { SignoffProvider, useSignoff } from './signoff.tsx';
import { asideMode, serverDetailId } from '../../lib/page-frame.ts';
import { StaleBanner } from './StaleBanner.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { Toaster } from '@/components/ui/sonner';
import { ConfirmHost } from '@/components/ui/use-confirm-ask';
import { CommandBar } from '@/components/spatial/CommandBar.tsx';
import { SpatialDesktopIcons } from '@/components/spatial/SpatialDesktopIcons.tsx';
import { SpatialCommandPortal, SpatialCommandSlotProvider } from '@/components/spatial/spatial-command-slot.tsx';
import { useUIStore } from '../../stores/ui-store.ts';
import { DASHBOARD_PATH } from '../../lib/nav-paths.ts';
import { spatialNpcCommand } from '../../lib/spatial-route.ts';

export function AppShell() {
  return (
    <SignoffProvider>
      <AppShellFrame />
    </SignoffProvider>
  );
}

function AppShellFrame() {
  const rememberSpatialRoute = useUIStore((s) => s.rememberSpatialRoute);
  const { waiting } = useSignoff();
  const location = useLocation();
  const isHome = location.pathname === DASHBOARD_PATH;
  const mode = asideMode(location.pathname);
  const serverId = serverDetailId(location.pathname);
  const decision =
    mode === 'overview' ||
    mode === 'certs' ||
    (mode === 'server' && waiting.some((cert) => cert.serverId === serverId));
  const [paletteOpen, setPaletteOpen] = useState(false);
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    const from = previousPath.current;
    if (from !== location.pathname) {
      rememberSpatialRoute(from);
      previousPath.current = location.pathname;
    }
  }, [location.pathname, rememberSpatialRoute]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <SpatialCommandSlotProvider>
      <div className="flex h-screen flex-col bg-transparent text-foreground">
        <Header onPalette={() => setPaletteOpen(true)} />
        <StaleBanner />
        {isHome ? null : (
          <SpatialCommandPortal>
            <CommandBar
              npcCommand={spatialNpcCommand(location.pathname)}
              showOpen={false}
              shortcutHint={false}
            />
          </SpatialCommandPortal>
        )}
        <div className="spatial-desktop-shell">
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto px-6 py-8 max-[860px]:px-4 max-[860px]:py-5">
            <Suspense fallback={<PageSkeleton />}>
              <div
                className={`np-page${mode ? '' : ' solo'}`}
                data-view={isHome ? 'overview' : 'page'}
                data-decision={decision ? '' : undefined}
              >
                <div className="np-main min-w-0">
                  <Outlet />
                  {mode ? null : <LimitsFoot />}
                </div>
                {mode ? <PageAside mode={mode} /> : null}
              </div>
            </Suspense>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
          </main>
          <SpatialDesktopIcons />
        </div>
        <ConfirmHost />
        <Toaster richColors position="top-right" />
      </div>
    </SpatialCommandSlotProvider>
  );
}
